import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError } from '~/lib/api';
import { cn } from '~/lib/cn';
import { ConfirmDialog } from '~/components/ui/ConfirmDialog';

interface RevokeActionProps {
  keyId: string;
  label: string;
  prefix: string;
  status: 'active' | 'revoked';
  onRevoked: () => void;
}

/**
 * Row-level action for an API key. Revoked rows show a muted chip only.
 * Active rows show an overflow (⋯) that opens a tiny menu; the one action
 * today is Revoke, which opens a destructive confirm dialog and POSTs to
 * /dashboard/api-keys/:id/revoke. The parent refreshes its own list on
 * success — this component owns only the UI state for the menu + dialog.
 */
export function RevokeAction({
  keyId,
  label,
  prefix,
  status,
  onRevoked,
}: RevokeActionProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Position the portaled menu directly under the trigger, right-aligned to it.
  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [menuOpen]);

  // Close on outside click, Escape, or page scroll — the portaled menu
  // doesn't move with scroll, so just dismiss it rather than re-measure.
  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent): void {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    function onScroll(): void {
      setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [menuOpen]);

  if (status === 'revoked') {
    return (
      <span className="font-mono inline-flex items-center rounded-full bg-surface-margin px-2 py-[3px] text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-watermark">
        Revoked
      </span>
    );
  }

  async function onConfirm(): Promise<void> {
    setError(null);
    setRevoking(true);
    try {
      await api(`/dashboard/api-keys/${keyId}/revoke`, { method: 'POST' });
      setConfirmOpen(false);
      onRevoked();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke key.');
    } finally {
      setRevoking(false);
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-watermark hover:bg-surface-margin hover:text-ink-primary',
          menuOpen && 'bg-surface-margin text-ink-primary',
        )}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <circle cx="3.5" cy="8" r="1.3" />
          <circle cx="8" cy="8" r="1.3" />
          <circle cx="12.5" cy="8" r="1.3" />
        </svg>
      </button>
      {menuOpen &&
        menuPos &&
        createPortal(
          // Portal the menu to <body> so it escapes the keys-list card's
          // overflow:hidden (used for the rounded corners). Position matches
          // the trigger each time it opens; scroll closes rather than
          // re-measures.
          <div
            ref={menuRef}
            role="menu"
            style={{ top: menuPos.top, right: menuPos.right }}
            className="fixed z-50 w-[140px] overflow-hidden rounded-md border border-rule-hairline bg-surface-plate shadow-lift-md"
          >
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setConfirmOpen(true);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[14px] text-stop hover:bg-stop-tint"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
              >
                <path d="M3 4h10M6 4V2.5h4V4M5 4l.5 9h5L11 4" />
              </svg>
              Revoke
            </button>
          </div>,
          document.body,
        )}

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => {
          if (revoking) return;
          setError(null);
          setConfirmOpen(false);
        }}
        onConfirm={() => void onConfirm()}
        title={`Revoke ${label}?`}
        tone="destructive"
        confirming={revoking}
        confirmLabel="Revoke key"
        body={
          <div className="flex flex-col gap-2">
            <p>
              Any service using this key will start getting 401s immediately.
              The prefix on record was{' '}
              <span className="font-mono text-ink-primary">{prefix}···</span>.
              This cannot be undone.
            </p>
            {error && (
              <p className="rounded border border-stop/20 bg-stop-tint px-2 py-1 text-[13px] text-stop">
                {error}
              </p>
            )}
          </div>
        }
      />
    </>
  );
}
